import React from 'react';
import {
  DarwinCol,
  DarwinRow,
  StatusChip,
  StatusChipColors,
} from 'components/darwin-ui';
import DarwinTypography from 'components/darwin-ui/Typography';
import { SettingOutlined } from '@ant-design/icons';
import { Control, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from '../../Users.module.css';
import { Form } from 'antd';
import { CreateUserFormData, CreateUserFormInputProps } from 'types/userTypes';
import { InputType } from 'types/genericTypes';
import { GenericInput } from 'components/ui/GenericInput';
import { LANGUAGE_OPTIONS_DATA } from 'constants/language';

const { Title } = DarwinTypography;

interface AdditionalSettingSectionProps {
  control: Control<CreateUserFormData>;
  errors: FieldErrors<CreateUserFormData>;
  canCreateEmailOrganizationRelation: boolean;
  isCreateForm: boolean;
  active?: boolean;
}

export const AdditionalSettingSection: React.FC<
  AdditionalSettingSectionProps
> = ({
  control,
  errors,
  canCreateEmailOrganizationRelation,
  isCreateForm,
  active = false,
}) => {
  const { t } = useTranslation();

  const firstColumnInputs: CreateUserFormInputProps = [
    {
      inputType: InputType.DROPDOWN,
      fieldName: 'language',
      label: t('create-user-form.language'),
      showRequiredAsterisk: false,
      inputRules: {},
      dropdownData: LANGUAGE_OPTIONS_DATA,
      disabled: !canCreateEmailOrganizationRelation,
    },
  ];

  const secondColumnInputs: CreateUserFormInputProps = [
    {
      inputType: InputType.DROPDOWN,
      fieldName: 'timeZone',
      label: t('create-user-form.time-zone'),
      showRequiredAsterisk: false,
      inputRules: {},
      dropdownData: [
        { label: 'GMT-8 (PST - Los Ángeles, San Francisco)', id: 'GMT-8' },
        { label: 'GMT-5 (EST - Nueva York, Miami, Toronto)', id: 'GMT-5' },
        { label: 'GMT-3 (Buenos Aires, São Paulo, Montevideo)', id: 'GMT-3' },
        { label: 'GMT+0 (Londres, Lisboa, Casablanca)', id: 'GMT+0' },
        { label: 'GMT+1 (Madrid, París, Berlín, Roma)', id: 'GMT+1' },
        {
          label: 'GMT+2 (Atenas, Estambul, El Cairo, Ciudad del Cabo)',
          id: 'GMT+2',
        },
        { label: 'GMT+3 (Moscú, Riad, Nairobi)', id: 'GMT+3' },
        { label: 'GMT+5:30 (India - Nueva Delhi, Mumbai)', id: 'GMT+5:30' },
        { label: 'GMT+7 (Bangkok, Hanoi, Yakarta)', id: 'GMT+7' },
        { label: 'GMT+8 (Pekín, Singapur, Hong Kong, Perth)', id: 'GMT+8' },
        { label: 'GMT+9 (Tokio, Seúl)', id: 'GMT+9' },
        { label: 'GMT+10 (Sídney, Melbourne)', id: 'GMT+10' },
      ],
      disabled: !canCreateEmailOrganizationRelation,
      enableSearchInDropdown: true,
    },
  ];

  return (
    <>
      <Title level={5} className={styles.userFormRowTitle}>
        <SettingOutlined /> {t('create-user-form.additional-settings')}
      </Title>
      <DarwinRow className={styles.userFormRow} gutter={16}>
        <DarwinCol span={12}>
          {firstColumnInputs.map(input => (
            <GenericInput
              key={input.fieldName}
              control={control}
              errors={errors}
              {...input}
            />
          ))}
        </DarwinCol>
        <DarwinCol span={12}>
          {secondColumnInputs.map(input => (
            <GenericInput
              key={input.fieldName}
              control={control}
              errors={errors}
              {...input}
            />
          ))}
        </DarwinCol>
        {!isCreateForm && (
          <DarwinCol span={12}>
            <Form.Item
              label={t('view-user-form.user-status-input')}
              className={styles.userFormInputTitle}
            >
              <StatusChip
                color={active ? StatusChipColors.green : StatusChipColors.red}
                label={active ? t('active') : t('inactive')}
              />
            </Form.Item>
          </DarwinCol>
        )}
      </DarwinRow>
    </>
  );
};
